import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Clock, Wallet, Truck, CheckCircle2, Star } from "lucide-react";

export default function Home() {
  return (
    <main>
      {/* HERO */}
      <section className="hero-mesh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-24 pb-12 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-[#1e429f] text-xs font-semibold uppercase tracking-wider mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1a56db] animate-pulse" />
                Instant quote · Free doorstep pickup
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
                Sell your old phone
                <br />
                in <span className="text-brand-gradient">60 seconds.</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                TechSaathi picks up your phone from home and pays you on the spot. No bargaining, no shop visits, no hidden deductions.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/sell" data-testid="lcost-hero-cta-sell-now">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto btn-brand text-white font-semibold text-base px-7 py-6 rounded-full shadow-lg shadow-[#1a56db]/20"
                  >
                    Get Instant Quote
                  </Button>
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center text-sm font-semibold text-slate-700 hover:text-[#1e429f] px-5 py-3"
                >
                  How it works →
                </a>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {[
                  { icon: ShieldCheck, label: "Fixed quote" },
                  { icon: Wallet, label: "Paid on pickup" },
                  { icon: Clock, label: "Same-day slots" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-start">
                    <Icon className="w-5 h-5 text-[#1a56db] mb-1.5" strokeWidth={2} />
                    <span className="text-xs sm:text-sm font-semibold text-slate-700">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-tr from-blue-100/60 to-transparent rounded-[2.5rem] blur-2xl" />
                <div className="relative bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xl shadow-slate-900/5">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Sample quote
                      </div>
                      <div className="font-display text-lg font-bold text-slate-900 mt-0.5">
                        iPhone 13 · Good condition
                      </div>
                    </div>
                    <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-[#1a56db]" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-[#1a56db] to-[#0e9f6e] rounded-2xl p-5 text-white">
                    <div className="text-[11px] font-semibold text-white/80 uppercase tracking-wider mb-1">
                      Your guaranteed price
                    </div>
                    <div className="font-display text-5xl font-black text-white tracking-tight">
                      ₹32,300
                    </div>
                  </div>
                  <div className="mt-5 space-y-2.5">
                    {[
                      "Base price (iPhone 13)",
                      "Screen · Minor scratches",
                      "Battery · Good",
                      "Body · Perfect",
                    ].map((row) => (
                      <div key={row} className="flex items-center justify-between text-xs sm:text-sm text-slate-600">
                        <span>{row}</span>
                        <CheckCircle2 className="w-4 h-4 text-blue-500" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-xl">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1e429f] mb-2">
              How TechSaathi works
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Three simple steps, zero surprises.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {[
              { n: "01", title: "Get an instant quote", body: "Pick your phone and tell us about its condition. Our guaranteed price appears in seconds.", icon: Star },
              { n: "02", title: "Book a free pickup", body: "Choose a date and 3-hour slot that works for you. Our executive comes to your door.", icon: Truck },
              { n: "03", title: "Get paid on the spot", body: "Device verified in minutes. Money hits your account via UPI or bank transfer, same day.", icon: Wallet },
            ].map(({ n, title, body, icon: Icon }) => (
              <div key={n} className="relative p-6 sm:p-7 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="absolute top-5 right-5 font-display text-xs font-bold text-slate-300 tracking-widest">
                  {n}
                </div>
                <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#1a56db]" strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-1.5">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-slate-50 footer-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Why Indians trust TechSaathi
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                We built TechSaathi because selling an old phone shouldn’t feel like a street-side bargain. Clear pricing, trained executives, instant payments.
              </p>
              <Link to="/sell" className="inline-block mt-6">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-5 font-semibold">
                  Start selling →
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { k: "100%", v: "Price we quote is price we pay" },
                { k: "60s", v: "To get a guaranteed quote" },
                { k: "15+", v: "Cities with free doorstep pickup" },
                { k: "UPI", v: "Instant payment at pickup" },
              ].map(({ k, v }) => (
                <div key={k} className="p-5 rounded-2xl bg-white border border-slate-200">
                  <div className="font-display text-3xl font-black text-brand-gradient inline-block">{k}</div>
                  <div className="text-sm text-slate-600 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="font-display font-semibold text-slate-700">
            L<span className="text-[#1a56db]">cost</span> · Sell your phone the smart way
          </div>
          <div>© {new Date().getFullYear()} TechSaathi India. All rights reserved.</div>
        </div>
      </footer>
    </main>
  );
}
